import { hot } from "react-hot-loader/root";
import React from "react";
import {
  Button,
  TextField,
  Columns,
  Column,
  Banner,
  Checkbox,
  Select,
} from "@sparkpost/matchbox";
import { Email, Help, VpnKey } from "@sparkpost/matchbox-icons";
import Section from "../components/Section";
import { i18n } from "../utils/utils";
import {
  activateLicense,
  deactivateLicense,
  saveFields,
  sendTestEmail,
} from "../utils/ajax";
import { useAppState } from "../utils/context";

const Settings = () => {
  const { fields, updateField, setLoading, displayNotice } = useAppState();

  /**
   * When activating the license.
   */
  const onActivateLicenseClick = async (e) => {
    setLoading(true);
    const result = await activateLicense(fields.license);

    if (result.success === true) {
      updateField("license_activated", true);
      displayNotice(result.message, "success");
    } else {
      updateField("license_activated", false);
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
  };

  /**
   * When deactivating the license
   */
  const onDeactivateLicenseClick = async (e) => {
    setLoading(true);
    const result = await deactivateLicense(fields.license);

    if (result.success === true) {
      updateField("license_activated", false);
      displayNotice(result.message, "success");
    } else {
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
  };

  /**
   * When saving the e-mail adress/name.
   */
  const onEmailSaveClick = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await saveFields({
      sender_mail_prefix: fields.sender_mail_prefix,
      sender_mail_suffix: fields.sender_mail_suffix,
      sender_name: fields.sender_name,
    });

    if (result.success === true) {
      displayNotice(result.message, "success");
    } else {
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
  };

  /**
   * When saving the reply-to e-mail adress/name.
   */
  const onReplyToSaveClick = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await saveFields({
      reply_to_mail: fields.reply_to_mail,
      reply_to_name: fields.reply_to_name,
    });

    if (result.success === true) {
      displayNotice(result.message, "success");
    } else {
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
  };

  /**
   * When changing the "Force sender e-mail" setting.
   */
  const onForceSenderEmailChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    updateField("force_sender_email", e.target.checked);

    const result = await saveFields({
      force_sender_email: e.target.checked,
    });

    if (result.success === true) {
      displayNotice(result.message, "success");
    } else {
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
  };

  /**
   * When changing the "Reply-to" enable/disable setting.
   */
  const onReplyToToggleChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    updateField("reply_to_enabled", e.target.checked);

    const result = await saveFields({
      reply_to_enabled: e.target.checked,
    });

    if (result.success === true) {
      displayNotice(result.message, "success");
    } else {
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
  };

  /**
   * When sending the test email.
   */
  const onTestEmailClick = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await sendTestEmail(fields.test_email_recipient);

    if (result.success === true) {
      displayNotice(result.message, "success");
    } else {
      displayNotice(result.message || result.error, "error");
    }

    setLoading(false);
  };

  const showAllSettingsBoxes = !!fields.license_activated;

  return (
    <>
      {showAllSettingsBoxes && (
        <>
          <Section
            title={i18n("Panels.Settings.EmailSender")}
            className="email-sender-settings"
          >
            {fields.sending_domain === fields.default_sending_domain && (
              <Banner
                mb="500"
                size="small"
                status="warning"
                onDismiss={() => {}}
              >
                {i18n("Fields.Settings.WarningDefaultDomain1")}{" "}
                <a
                  href="https://docs.wpmailpro.com/docs/configure-sending-domain/"
                  target="_blank"
                >
                  {i18n("Fields.Settings.WarningDefaultDomain2")}
                </a>
              </Banner>
            )}
            <Columns>
              <Column width={2 / 5}>
                <TextField
                  id="sender_mail"
                  label={i18n("Fields.Settings.EmailSenderMail")}
                  placeholder="john.doe"
                  name="sender_mail_prefix"
                  value={fields.sender_mail_prefix}
                  disabled={!fields.domain_dkim_record_is_valid}
                  onChange={(e) => {
                    updateField(e.target.name, e.target.value);
                  }}
                  connectRight={
                    <EmailDomainSelectorDropdown
                      fields={fields}
                      updateField={updateField}
                    />
                  }
                />
              </Column>
              <Column width={2 / 5}>
                <TextField
                  id="sender_name"
                  label={i18n("Fields.Settings.EmailSenderName")}
                  placeholder="John Doe"
                  name="sender_name"
                  value={fields.sender_name}
                  onChange={(e) => {
                    updateField(e.target.name, e.target.value);
                  }}
                />
              </Column>
              <Column className="align-bottom">
                <Button
                  size="default"
                  width="100%"
                  variant="outline"
                  color="blue"
                  onClick={onEmailSaveClick}
                  mutedOutline
                >
                  {i18n("Button.Save")}
                </Button>
              </Column>
            </Columns>
            {/* {fields.sender_mail !== "" &&
              fields.domain_dkim_record_is_valid &&
              !fields.sender_mail.includes(fields.sending_domain) && (
                <Banner
                  mt="300"
                  size="small"
                  status="warning"
                  onDismiss={() => {}}
                >
                  {i18n("Fields.Settings.EmailSenderMailDesc")}
                </Banner>
              )} */}
            <div className="force-sender-setting">
              <Checkbox
                id="force_sender_email"
                name="force_sender_email"
                checked={fields.force_sender_email}
                label={i18n("Fields.Settings.ForceSenderEmail")}
                helpText={i18n("Fields.Settings.ForceSenderEmailDesc")}
                onChange={onForceSenderEmailChange}
              />
            </div>
          </Section>
          <Section
            title={i18n("Fields.Settings.ReplyToEnabled")}
            className="reply-to-section"
          >
            <Checkbox
              id="reply_to_enabled"
              name="reply_to_enabled"
              checked={fields.reply_to_enabled}
              label={i18n("Fields.Settings.ReplyToEnabledLabel")}
              onChange={onReplyToToggleChange}
            />
            {fields.reply_to_enabled && (
              <Columns mt={300}>
                <Column width={2 / 5}>
                  <TextField
                    id="reply_to_mail"
                    label={i18n("Fields.Settings.EmailSenderMail")}
                    placeholder="john.doe@mail.com"
                    name="reply_to_mail"
                    type="email"
                    value={fields.reply_to_mail}
                    onChange={(e) => {
                      updateField(e.target.name, e.target.value);
                    }}
                  />
                </Column>
                <Column width={2 / 5}>
                  <TextField
                    id="reply_to_name"
                    label={i18n("Fields.Settings.EmailSenderName")}
                    placeholder="John Doe"
                    name="reply_to_name"
                    value={fields.reply_to_name}
                    onChange={(e) => {
                      updateField(e.target.name, e.target.value);
                    }}
                  />
                </Column>
                <Column className="align-bottom">
                  <Button
                    size="default"
                    width="100%"
                    variant="outline"
                    color="blue"
                    onClick={onReplyToSaveClick}
                    mutedOutline
                  >
                    {i18n("Button.Save")}
                  </Button>
                </Column>
              </Columns>
            )}
          </Section>
          <Section title={i18n("Panels.DNS.TestEmail")}>
            <form className="restricted-width" onSubmit={onTestEmailClick}>
              <TextField
                id="test_email_recipient"
                label={i18n("Fields.DNS.TestEmailRecipient")}
                placeholder="some@email.com"
                name="test_email_recipient"
                value={fields.test_email_recipient || ""}
                onChange={(e) => {
                  updateField(e.target.name, e.target.value);
                }}
                connectRight={
                  <Button
                    size="default"
                    variant="outline"
                    color="blue"
                    disabled={!fields.test_email_recipient}
                    onClick={onTestEmailClick}
                  >
                    <Button.Icon as={Email} size={20} mr="200"></Button.Icon>
                    {i18n("Button.Send")}
                  </Button>
                }
              />
            </form>
          </Section>
        </>
      )}
      <Section title={i18n("Panels.Settings.License")}>
        <div className="restricted-width xl">
          <TextField
            id="license"
            label={i18n("Fields.Settings.PluginLicense")}
            placeholder={i18n("Fields.Settings.PluginLicense")}
            name="license"
            value={fields.license}
            //readOnly={fields.license_activated}
            onChange={(e) => {
              updateField(e.target.name, e.target.value);
            }}
            connectRight={
              <>
                <Button
                  size="default"
                  onClick={onActivateLicenseClick}
                  mutedOutline
                  color="blue"
                  //disabled={fields.license_activated || fields.license === ""}
                >
                  {i18n("Button.LicenseActivate")}
                </Button>
                <Button
                  size="default"
                  onClick={onDeactivateLicenseClick}
                  color="red"
                  //disabled={!fields.license_activated || fields.license === ""}
                >
                  {i18n("Button.LicenseDeactivate")}
                </Button>
              </>
            }
          />
        </div>
        {!fields.license_activated && (
          <p className="free-license-callout">
            <a
              href="https://wpmailpro.com/pricing?upgrade=true"
              target="_blank"
            >
              <VpnKey size={22} /> {i18n("Text.NeedFreeLicense")}
            </a>
          </p>
        )}
      </Section>
      <p className="help-link">
        <a href="https://docs.wpmailpro.com/docs/settings/" target="_blank">
          <Help size={18} /> {i18n("Text.NeedHelp")}
        </a>
      </p>
    </>
  );
};

/**
 * E-mail domain selector dropdown (custom domain OR default domain).
 */
export const EmailDomainSelectorDropdown = ({ fields, updateField }) => {
  // If no custom domain, return default domain disabled text field.
  if (!fields.domain_dkim_record_is_valid) {
    return (
      <TextField
        id="sender_mail_suffix"
        value={`@${fields.default_sending_domain}`}
        disabled
      />
    );
  }

  // Otherwise, return a dropdown.
  return (
    <Select
      className="sender-mail-suffix"
      name="sender_mail_suffix"
      value={fields.sender_mail_suffix}
      onChange={(e) => updateField("sender_mail_suffix", e.target.value)}
      options={[
        {
          value: fields.sending_domain,
          label: `@${fields.sending_domain}`,
        },
        {
          value: fields.default_sending_domain,
          label: `@${fields.default_sending_domain}`,
        },
      ]}
    />
  );
};

export default hot(Settings);
